import { toRichText } from './mappers';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * TRANSITIONAL. While this is `true` a swept field is readable twice: under its own name, and in
 * the family array it was swept into.
 *
 * That is what lets each component move to the plain names on its own schedule rather than every
 * reader changing in one commit.
 *
 * Flipping it to `false` is the last step of the migration, not its safety net. It stops the arrays
 * being emitted; it does not by itself make a stale reader fail. A component's content type is a
 * hand-written claim rather than anything derived from this adapter, so a declared
 * `notifications?: unknown` still compiles and simply reads `undefined`, which is the silent
 * failure this whole change exists to remove. What makes a stale reader fail is deleting that
 * declaration from its own content type, which each component's story does as it migrates. Flip
 * this once the last one has.
 */
const KEEP_FAMILY_ARRAYS = true;

/**
 * The family array that holds swept rich text. An entry here is not the value: the value is the
 * block array inside it, which is why `richTextByRole` exists alongside `familyEntryByRole`.
 *
 * Offered as HTML, like all rich text a component reads. Converted here rather than left to
 * `serializeRichText`, because this list is the only thing that says the value is rich text. Once
 * out of it, an empty entry's `content: []` looks like any empty list.
 *
 * Recognised by the array it sits in, because that is the only marker the stored data carries.
 * Entries have no `_type` in the dataset, whatever the generated types say:
 * `{ "_key": "helpText", "content": [], "role": "helpText" }` is a real entry, verbatim. The name is
 * reliable: every one of the 380 rich-text members in `component-map.json` lives in a list called
 * `richText`, and no rich-text member lives in any other list. It cannot be recognised by shape,
 * since `notificationField` also has a `content` member and a notification carrying only a body
 * would be unwrapped as though it were rich text.
 */
const RICH_TEXT_FAMILY = 'richText';

/** The generated type's name for the same entry, honoured in case a later migration writes one. */
const RICH_TEXT_ENTRY = 'richTextEntry';

/** Bookkeeping Sanity or the migration adds, which is not content. Mirrors `familyEntryByRole`. */
const BOOKKEEPING = new Set(['role', '_key', '_type']);

const isFamilyEntry = (value: unknown): value is Record<string, unknown> & { role: string } =>
  isObject(value) && typeof value.role === 'string' && value.role.length > 0;

/** A family array: every element an object with a non-empty string `role`. See the gate below. */
export const isFamilyList = (value: unknown): value is Array<Record<string, unknown> & { role: string }> =>
  Array.isArray(value) && value.length > 0 && value.every(isFamilyEntry);

const isPortableTextArray = (value: unknown[]): boolean => value.some(item => isObject(item) && item._type === 'block');

const sameMembers = (a: Record<string, unknown>, b: Record<string, unknown>): boolean => {
  const keys = Object.keys(b);
  return keys.length === Object.keys(a).length && keys.every(key => a[key] === b[key]);
};

/**
 * Inline objects inside a block can hold families of their own: an `AccordionItem` keeps its answer
 * in `richText`, a `RowItem` its label. Sitecore hands those over by name (its adapter lifts a
 * multilist item's fields), so they are un-swept too, at any depth. Portable Text and the family
 * arrays themselves are left as they are, and anything with nothing to un-sweep comes back as the
 * same reference, so only the paths that change are copied.
 */
const unsweepNested = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    if (isFamilyList(value) || isPortableTextArray(value)) return value;
    let changed = false;
    const next = value.map(item => {
      const result = unsweepNested(item);
      if (result !== item) changed = true;
      return result;
    });
    return changed ? next : value;
  }
  if (isObject(value)) {
    const next = unsweepFamilies(value);
    return sameMembers(value, next) ? value : next;
  }
  return value;
};

/**
 * Reads swept fields back out of their family arrays, under the names they had before the sweep.
 *
 * ADR-0068 decision 8 collapsed same-shaped fields into one array per shape, so `HeroCard`'s
 * `ribbonContent` became an entry of `richText` with `role === 'ribbonContent'`, and a step's
 * `notification` an entry of `notifications`. That is a storage decision: it saved a schema entry
 * per field across 1,860 fields. It is not something a component should have to know, and Sitecore
 * has no equivalent, so a component written against it renders nothing on the Sitecore lane.
 *
 * Needs no table, because the sweep kept the original field name on every entry as `role`. Going
 * back is a rule, not a lookup: each entry becomes a field named by its own role.
 *
 * The gate is deliberately narrow. Only an array whose every element is an object with a non-empty
 * string `role` is treated as a family, and in the generated schema exactly ten types declare a
 * `role`, all of them family member types. No section or document has one.
 *
 * This is the Sanity half of one change. The Sitecore half lifts that CMS's field groups, so that
 * both hand a component the same `title` and the same `notification`.
 */
export const unsweepFamilies = (content: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  const swept: Array<{ role: string; value: unknown }> = [];
  const timesSeen = new Map<string, number>();
  /**
   * Names already spoken for on this block: its own fields, and the family arrays themselves.
   *
   * Collected whether or not the arrays are being kept, so that flipping `KEEP_FAMILY_ARRAYS`
   * changes what is emitted but never which roles are offered. Reading `out` instead would make a
   * role that matches its own array's name unreadable today and readable after the flip, which is
   * the sort of difference that only shows up long after anyone is looking for it.
   */
  const taken = new Set(Object.keys(content));

  for (const [key, value] of Object.entries(content)) {
    if (isFamilyList(value)) {
      if (KEEP_FAMILY_ARRAYS) out[key] = value;

      for (const entry of value) {
        const isRichText = key === RICH_TEXT_FAMILY || entry._type === RICH_TEXT_ENTRY;
        const payload = isRichText
          ? (toRichText(entry.content) ?? '')
          : Object.fromEntries(Object.entries(entry).filter(([member]) => !BOOKKEEPING.has(member)));

        swept.push({ role: entry.role, value: payload });
        timesSeen.set(entry.role, (timesSeen.get(entry.role) ?? 0) + 1);
      }
      continue;
    }

    out[key] = unsweepNested(value);
  }

  for (const { role, value } of swept) {
    // One role claimed by two entries cannot be resolved by name, so neither is offered and the
    // array stays the only way to read them. The schema forbids this, which is why it is a guard
    // rather than a case: a dataset that breaks its own validation should not pick a winner here.
    if (timesSeen.get(role) !== 1) continue;
    // A name the block already uses wins, whether that is a real field or one of the family arrays.
    // Nothing in the schema does either today.
    if (taken.has(role)) continue;
    out[role] = value;
  }

  return out;
};
